import styles from "./Modals.module.scss";
import React from "react";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { useTranslation } from "react-i18next";
import { Button } from "@whatslly/ui-components/v2/Button";
import { loginListener } from "@whatslly/commons/loginListener";
import { LoginCommands } from "@whatslly/commons/loginCommands";
import { getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { useLocalObservable } from "mobx-react-lite";
import { useDependencyInjector } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { GetAppsRedirectUrlResponseType } from "@whatslly/types/integrations/auth/res/GetAppsRedirectUrlResponse";
import type { GetAppsTokenResponsType } from "@whatslly/types/integrations/auth/res/GetAppsTokenResponse";
import { parseOrReturnError } from "../../../services/utils";
import type { DestinationFormProps } from "../utils/DestinationsListTypes";
export const PipedriveModalForm: React.FC<DestinationFormProps<"pipedrive">> = ({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const logger = useDependencyInjector<FrontLogger>(FrontLogger);

  const store = useLocalObservable(() => {
    return {
      reconnect: false,
      async login() {
        const urlSearchParams = new URLSearchParams();
        urlSearchParams.set("appName", "pipedrive");
        urlSearchParams.set("chatApp", "popup-web");
        urlSearchParams.set("userId", "");
        const getRedRes = await getJSON({
          urlSuffix: `/integrations/auth/redirect?${urlSearchParams.toString()}`,
          pod: import.meta.env.VITE_API_DEFAULT_POD?.toString() ?? "us1",
          fetchMethod: fetch,
        });
        const res: GetAppsRedirectUrlResponseType = await getRedRes.json();

        const top = (window.screen.availHeight - 500) / 2;
        const left = (window.screen.availWidth - 500) / 2;
        const options: Record<string, number | boolean> = {
          width: 800,
          height: 800,
          top: top > 0 ? top : 0,
          left: left > 0 ? left : 0,
          location: true,
          resizable: true,
          statusbar: true,
          toolbar: false,
        };

        const winOpt = Object.keys(options)
          .map((op) => `${op}=${Number(options[op])}`)
          .join(",");
        const popup = window.open(res.url, "Login", winOpt);
        const appTokensRes = await new Promise<any & { success: true }>((resolve, reject) => {
          const usubscribe = loginListener.on(LoginCommands.LOGIN_COMPLETED, (data) => {
            clearInterval(interval);
            resolve(data);
            usubscribe();
          });
          const interval = setInterval(() => {
            if (popup?.closed) {
              clearInterval(interval);
              usubscribe();
              reject(new Error(t("login.login_failed")));
            }
          }, 1000);
        });

        const body = await postJSON({
          urlSuffix: "/integrations/auth/get-apps-token",
          body: JSON.stringify({
            code: appTokensRes.code,
            state: appTokensRes.state,
          }),
        }).then((r) => parseOrReturnError<GetAppsTokenResponsType>(r, logger));
        if (!body.success || !body.tokens || !body.integrationData) return;

        onSubmitForm({
          type: "pipedrive" as const,
          integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
          settings: {
            storeMedia: true,
            tokens: body.tokens,
            companyId: body.integrationData.companyId,
            companyDomain: body.integrationData.companyDomain,
            userId: body.integrationData.userId,
            user: body.integrationData.user,
          },
        });
      },
    };
  });

  return (
    <div>
      {settings?.userId && settings?.user && !store.reconnect ? (
        <div className={styles.reconnectSalesforce}>
          <div className={styles.reconnectSalesforceText}>{`User Id: ${settings?.userId}`}</div>
          <div className={styles.reconnectSalesforceText}>{`User: ${settings?.user}`}</div>
          <div className={styles.reconnectSalesforceText}>{`Company Domain: ${settings.companyDomain}`}</div>
          <div className={styles.reconnectSalesforceButton}>
            <Button onClick={() => (store.reconnect = true)}>{t("destinations_page.pipedrive.reconnect")}</Button>
          </div>
        </div>
      ) : (
        <div className={styles.hubspotConnect}>
          <Button onClick={() => store.login()}>{t("destinations_page.pipedrive.connect")}</Button>
        </div>
      )}
    </div>
  );
};
